// src/redux/slices/chat/chatSlice.ts

import { createSlice, PayloadAction, Slice } from '@reduxjs/toolkit';

// Import from new modular files
import { initialChatState } from './chatInitialState';
import { CHAT_API_ENDPOINTS, CHAT_CONFIG } from './chatConfig';
import {
  Message,
  Conversation,
  ChatState,
  ConversationStateUpdate,
} from './chatTypes';

// Import all async thunks from chatThunks.ts
import {
  sendMessage,
  fetchConversationsMetadata,
  fetchMoreConversations,
  startListeningForConversations,
  fetchConversationMessagesThunk,
  deleteConversationThunk,
  deleteMessageThunk,
} from './chatThunks';

const initialState: ChatState = initialChatState;

/**
 * The chat slice managing chat-related state and actions.
 */
const chatSlice: Slice<ChatState> = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    /**
     * Adds a new message to the chat.
     */
    addMessage: (state, action: PayloadAction<Message>) => {
      try {
        // Create a deep copy of the message to ensure all properties are preserved
        const messageCopy = {
          ...action.payload,
          // Explicitly clone nested objects to prevent reference issues
          extra_parameters: action.payload.extra_parameters 
            ? { ...action.payload.extra_parameters } 
            : {}
        };
        
        // Enhancement for Sein-II messages
        // This provides a backup method if the listeners didn't properly set the parameters
        if (messageCopy.thinkingType === 'fraktal' && 
            messageCopy.id.includes('_response') && 
            messageCopy.sender === 'bot') {
          
          // Always ensure extra_parameters exists
          if (!messageCopy.extra_parameters) {
            messageCopy.extra_parameters = {};
          }
          
          // Handle special case for known Sein-II conversations
          const isKnownSeinIIMessage = messageCopy.conversationId === 'bdbe2d44-dbfd-4caf-bd84-da2aab228b71';
          
          if (isKnownSeinIIMessage) {
            messageCopy.extra_parameters.sein_chain_version = 'SEIN_2';
            messageCopy.extra_parameters.fraktal_complexity = 'sein_complexity_2';
          }
        }
        
        // Add the message to the state
        state.messages.push(messageCopy);
        
        // Update currentConversationId if this is a new conversation
        if (!state.currentConversationId && messageCopy.conversationId) {
          state.currentConversationId = messageCopy.conversationId;
        }
      } catch (error) {
        console.error('Error in addMessage reducer:', error);
        // Add fallback logic for error case
        state.messages.push(action.payload);
      }
    },

    /**
     * Adds a conversation to the conversations array.
     */
    addConversation: (state, action: PayloadAction<Conversation>) => {
      // Check if conversation already exists
      const existingIndex = state.conversations.findIndex((c: Conversation) => c.id === action.payload.id);
      if (existingIndex >= 0) {
        // Update existing conversation
        state.conversations[existingIndex] = action.payload;
      } else {
        // Add new conversation
        state.conversations.push(action.payload);
      }
      
      // Sort conversations by lastMessageTimestamp (newest first)
      state.conversations.sort((a: Conversation, b: Conversation) => 
        new Date(b.lastMessageTimestamp).getTime() - new Date(a.lastMessageTimestamp).getTime()
      );
    },

    /**
     * Updates a conversation's state.
     */
    updateConversationState: (
      state,
      action: PayloadAction<ConversationStateUpdate>
    ) => {
      const { conversationId, status, progress, type, processingType } = action.payload;
      
      // Update the conversationStates field
      if (!state.conversationStates[conversationId]) {
        state.conversationStates[conversationId] = {
          status: 'idle',
          progress: '',
          type: type || 'reflection', // Default to reflection if not specified
        };
      }

      state.conversationStates[conversationId].status = status;
      state.conversationStates[conversationId].progress = progress;
      
      // Only update type if provided
      if (type) {
        state.conversationStates[conversationId].type = type;
      }
      
      // Update processingType if provided
      if (processingType !== undefined) {
        state.conversationStates[conversationId].processingType = processingType;
      }
      
      // Update lastUpdated timestamp
      state.conversationStates[conversationId].lastUpdated = new Date().toISOString();
    },

    /**
     * Marks a message as loading.
     */
    setMessageLoading: (state, action: PayloadAction<string>) => {
      const message = state.messages.find((msg: Message) => msg.id === action.payload);
      if (message) {
        message.loading = true;
        state.loading = true;
      }
    },

    /**
     * Marks a message as errored.
     */
    setMessageError: (state, action: PayloadAction<string>) => {
      const message = state.messages.find((msg: Message) => msg.id === action.payload);
      if (message) {
        message.error = true;
        message.loading = false;
      }
      state.loading = false;
    },

    /**
     * Clears the current chat.
     * @param keepConversationId Optional boolean to keep the current conversation ID (default: false)
     */
    clearChat: (state, action: PayloadAction<boolean | undefined> = { payload: undefined, type: 'chat/clearChat' }) => {
      state.messages = [];
      state.loading = false;
      state.error = null;
      
      // Only reset the conversation ID if not explicitly told to keep it
      if (!action.payload) {
        state.currentConversationId = undefined;
      }
    },

    /**
     * Sets the current conversation ID.
     */
    setCurrentConversation: (state, action: PayloadAction<string>) => {
      state.currentConversationId = action.payload;
    },

    /**
     * Sets the chat status ('idle' or 'busy').
     */
    setChatStatus: (state, action: PayloadAction<'idle' | 'busy'>) => {
      console.log('Setting chat status to:', action.payload);
    },

    /**
     * Removes a conversation from the conversations array.
     */
    removeConversation: (state, action: PayloadAction<string>) => {
      const conversationId = action.payload;
      state.conversations = state.conversations.filter((c: Conversation) => c.id !== conversationId);
      
      // Also clean up the conversation state
      if (state.conversationStates[conversationId]) {
        delete state.conversationStates[conversationId];
      }
    },

    /**
     * Removes messages after a given timestamp.
     */
    removeMessagesAfterTimestamp: (state, action: PayloadAction<string>) => {
      const timestamp = action.payload;
      state.messages = state.messages.filter((message: Message) => 
        new Date(message.createdAt).getTime() < new Date(timestamp).getTime()
      );
    },

    /**
     * Smart merge of conversations that preserves pagination state.
     * Used by real-time listeners to update the conversation list without destroying paginated data.
     */
    replaceConversations: (state, action: PayloadAction<Conversation[]>) => {
      const listenerConversations = action.payload;
      const currentConversations = state.conversations;
      
      // If no current conversations (initial load), just replace
      if (currentConversations.length === 0) {
        state.conversations = listenerConversations;
        return;
      }
      
      // If listener has significantly fewer conversations than what we've paginated, do smart merge
      // Allow for small discrepancies (1-2 conversations) due to timestamp precision
      if (listenerConversations.length < currentConversations.length - 2) {
        // Create a map of listener conversations by ID for quick lookup
        const listenerMap = new Map(listenerConversations.map(c => [c.id, c]));
        
        // Update existing conversations with new data from listener, keep all existing ones
        const updatedConversations = currentConversations.map(existing => {
          const updated = listenerMap.get(existing.id);
          return updated || existing; // Use updated version if available, otherwise keep existing
        });
        
        // Add any truly new conversations from listener that aren't in current list
        const currentIds = new Set(currentConversations.map(c => c.id));
        const newConversations = listenerConversations.filter(lc => !currentIds.has(lc.id));
        
        // Merge: combine all conversations and sort by timestamp to ensure chronological order
        const mergedConversations = [...newConversations, ...updatedConversations]
          .sort((a, b) => new Date(b.lastMessageTimestamp).getTime() - new Date(a.lastMessageTimestamp).getTime());
        
        state.conversations = mergedConversations;
      } else {
        // Listener has same or more conversations, safe to replace and sort
        state.conversations = listenerConversations
          .sort((a, b) => new Date(b.lastMessageTimestamp).getTime() - new Date(a.lastMessageTimestamp).getTime());
      }
    },

    /**
     * Appends conversations to the existing array for pagination.
     * Does NOT re-sort - assumes conversations are already in chronological order.
     * NOTE: This reducer is now only used by the fetchMoreConversations.fulfilled case
     * and should not be called manually to avoid double-dispatching.
     */
    appendConversations: (
      state, 
      action: PayloadAction<{ 
        conversations: Conversation[]; 
        hasMore: boolean; 
        lastTimestamp?: string; 
      }>
    ) => {
      const { conversations, hasMore, lastTimestamp } = action.payload;
      
      // Append new conversations to existing array (no re-sorting to preserve Firebase order)
      state.conversations.push(...conversations);
      
      // Update pagination state
      state.pagination.hasMore = hasMore;
      state.pagination.lastTimestamp = lastTimestamp;
      state.pagination.loadingMore = false;
    },

    /**
     * Sets pagination loading state.
     * NOTE: This is now handled automatically by the fetchMoreConversations thunk cases
     * and should rarely need to be called manually.
     */
    setPaginationLoading: (state, action: PayloadAction<boolean>) => {
      state.pagination.loadingMore = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // sendMessage cases
      .addCase(sendMessage.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // fetchConversationsMetadata cases (Initial load)
      .addCase(fetchConversationsMetadata.pending, (state) => {
        state.loadingHistory = true;
        state.error = null;
      })
      .addCase(fetchConversationsMetadata.fulfilled, (state, action) => {
        state.loadingHistory = false;
        const { conversations, hasMore, lastTimestamp } = action.payload;
        console.log(`Loaded ${conversations.length} conversations metadata (initial page)`);
        
        // REPLACE conversations array (initial load)
        state.conversations = conversations;
        state.pagination = {
          hasMore,
          lastTimestamp,
          loadingMore: false,
        };
        
        console.log(`Initial conversations loaded and pagination state updated`);
      })
      .addCase(fetchConversationsMetadata.rejected, (state, action) => {
        state.loadingHistory = false;
        state.error = action.payload as string;
      })

      // fetchMoreConversations cases (Pagination)
      .addCase(fetchMoreConversations.pending, (state) => {
        state.pagination.loadingMore = true;
        state.error = null;
      })
      .addCase(fetchMoreConversations.fulfilled, (state, action) => {
        const { conversations, hasMore, lastTimestamp } = action.payload;
        console.log(`Loaded ${conversations.length} more conversations (pagination)`);
        
        // APPEND to existing conversations array (pagination)
        state.conversations.push(...conversations);
        
        // Update pagination state
        state.pagination.hasMore = hasMore;
        state.pagination.lastTimestamp = lastTimestamp;
        state.pagination.loadingMore = false;
        
        console.log(`Pagination completed. Total conversations: ${state.conversations.length}`);
      })
      .addCase(fetchMoreConversations.rejected, (state, action) => {
        state.pagination.loadingMore = false;
        state.error = action.payload as string;
        console.error('Error loading more conversations:', action.payload);
      })

      // fetchConversationMessagesThunk cases
      .addCase(fetchConversationMessagesThunk.pending, (state) => {
        state.loadingMessages = true;
        state.error = null;
      })
      .addCase(fetchConversationMessagesThunk.fulfilled, (state, action) => {
        state.loadingMessages = false;
        
        // Log received messages for debugging
        console.log('fetchConversationMessagesThunk.fulfilled - received messages:', 
          action.payload.map((m: Message) => ({
            id: m.id,
            thinkingType: m.thinkingType,
            extra_parameters: m.extra_parameters,
            text: m.text.substring(0, 20) + '...'
          }))
        );
        
        // Check for any Sein-II messages
        const seinIIMessages = action.payload.filter((m: Message) =>
          m.thinkingType === 'fraktal' && 
          (m.extra_parameters?.sein_chain_version === 'SEIN_2' || 
           m.extra_parameters?.fraktal_complexity === 'sein_complexity_2')
        );
        
        if (seinIIMessages.length > 0) {
          console.log('Found Sein-II messages in payload:', seinIIMessages.length);
          console.log('Sein-II message details:', seinIIMessages.map((m: Message) => ({
            id: m.id,
            extra_parameters: m.extra_parameters
          })));
        }
        
        // Assign the messages to state, ensuring we keep all fields
        state.messages = action.payload.map((message: Message) => ({
          ...message,
          // Ensure thinkingType and extra_parameters are preserved exactly as they came from the service
          thinkingType: message.thinkingType,
          extra_parameters: message.extra_parameters
        }));
        
        // Also update the messages in the conversations array
        if (state.currentConversationId) {
          // Update conversations array
          const conversationIndex = state.conversations.findIndex((c: Conversation) => c.id === state.currentConversationId);
          if (conversationIndex >= 0) {
            state.conversations[conversationIndex].messages = action.payload;
          }
        }
      })
      .addCase(fetchConversationMessagesThunk.rejected, (state, action) => {
        state.loadingMessages = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  addMessage,
  setMessageLoading,
  setMessageError,
  clearChat,
  setCurrentConversation,
  setChatStatus,
  updateConversationState,
  addConversation,
  removeConversation,
  removeMessagesAfterTimestamp,
  replaceConversations,
  appendConversations,
  setPaginationLoading,
} = chatSlice.actions;

export default chatSlice.reducer;